import { RPC_ENDPOINT } from "@solomon-apps/config";
import { TOKEN_2022_PROGRAM_ID_STRING, TOKEN_PROGRAM_ID_STRING } from "@solomon-labs/constants";

import type { ParsedTokenAccount } from "./parseTokenBalances";

/**
 * Direct RPC fallback for classic SPL and Token-2022 accounts. JSON parsing is
 * kept here because this path runs when the binary account decoder fails.
 */
export async function fetchSPLTokenAccountsFallback(ownerAddress: string): Promise<ParsedTokenAccount[]> {
    const results = await Promise.all([
        fetchTokenAccountsForProgram(ownerAddress, TOKEN_PROGRAM_ID_STRING),
        fetchTokenAccountsForProgram(ownerAddress, TOKEN_2022_PROGRAM_ID_STRING)
    ]);
    return results.flat();
}

async function fetchTokenAccountsForProgram(ownerAddress: string, programId: string): Promise<ParsedTokenAccount[]> {
    const response = await fetch(RPC_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            jsonrpc: "2.0",
            id: 1,
            method: "getTokenAccountsByOwner",
            params: [ownerAddress, { programId }, { encoding: "jsonParsed" }]
        })
    });

    if (!response.ok) {
        throw new Error(`Token account RPC request failed with HTTP ${response.status}`);
    }

    const data = await response.json();

    if (!isRecord(data)) {
        throw new Error("Invalid token account RPC response");
    }

    if (data.error) {
        throw new Error(getRpcErrorMessage(data.error));
    }

    const accounts: ParsedTokenAccount[] = [];
    const values = getRpcValues(data);
    for (const item of values) {
        const info = getParsedTokenInfo(item);
        if (info) {
            accounts.push({
                mint: info.mint,
                amount: info.amount
            });
        }
    }

    return accounts;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null;
}

function getRpcErrorMessage(error: unknown): string {
    if (isRecord(error) && typeof error.message === "string") {
        return error.message;
    }

    return "Token account RPC request failed";
}

function getRpcValues(data: Record<string, unknown>): unknown[] {
    if (!isRecord(data.result) || !Array.isArray(data.result.value)) {
        throw new Error("Invalid token account RPC response");
    }

    return data.result.value;
}

function getParsedTokenInfo(item: unknown): { mint: string; amount: string } | null {
    if (
        !isRecord(item) ||
        !isRecord(item.account) ||
        !isRecord(item.account.data) ||
        !isRecord(item.account.data.parsed)
    ) {
        return null;
    }

    const info = item.account.data.parsed.info;
    if (!isRecord(info) || typeof info.mint !== "string" || !isRecord(info.tokenAmount)) {
        return null;
    }

    const amount = info.tokenAmount.amount;
    if (typeof amount !== "string" || !/^\d+$/.test(amount)) {
        return null;
    }

    return { mint: info.mint, amount };
}
